import React, { useEffect, useRef, useState } from "react";
import { svgAPng } from "./usuario/util";

const MIN = 1, MAX = 6;

// Envuelve un dibujo SVG para poder acercarlo: rueda del raton, botones +/−, arrastrar para moverse y doble clic para volver. Un clic sin arrastrar llega al dibujo.
const ConZoom = ({ children }) => {
    const caja = useRef(null);
    const [z, setZ] = useState({ k: 1, x: 0, y: 0 });
    const raiz = useRef(null);
    const [pantalla, setPantalla] = useState(false);
    const arrastre = useRef(null);
    const huboArrastre = useRef(false);

    // Mantiene el dibujo dentro del recuadro
    const limitar = (k, x, y, w, h) => ({ k, x: Math.min(0, Math.max(w - w * k, x)), y: Math.min(0, Math.max(h - h * k, y)) });
    const zoomEn = (factor, cx, cy) => setZ(p => {
        const el = caja.current; if (!el) return p;
        const k = Math.min(MAX, Math.max(MIN, p.k * factor));
        if (k === p.k) return p;
        const { width: w, height: h } = el.getBoundingClientRect();
        const r = k / p.k;
        return limitar(k, cx - (cx - p.x) * r, cy - (cy - p.y) * r, w, h);
    });
    const centro = () => { const el = caja.current; const b = el ? el.getBoundingClientRect() : { width: 0, height: 0 }; return [b.width / 2, b.height / 2]; };

    useEffect(() => {
        const f = () => { setPantalla(document.fullscreenElement === raiz.current); setZ({ k: 1, x: 0, y: 0 }); };
        document.addEventListener("fullscreenchange", f);
        return () => document.removeEventListener("fullscreenchange", f);
    }, []);
    const alternarPantalla = () => {
        try { if (document.fullscreenElement) document.exitFullscreen(); else if (raiz.current.requestFullscreen) raiz.current.requestFullscreen(); } catch (e) { /* el navegador no lo permite */ }
    };

    useEffect(() => {
        const el = caja.current;
        if (!el) return undefined;
        const rueda = (e) => {
            if (!e.ctrlKey && !e.shiftKey && z.k === 1 && !e.altKey) return;      // sin modificador y sin zoom, la rueda sigue moviendo la pagina
            e.preventDefault();
            const b = el.getBoundingClientRect();
            zoomEn(e.deltaY < 0 ? 1.2 : 1 / 1.2, e.clientX - b.left, e.clientY - b.top);
        };
        el.addEventListener("wheel", rueda, { passive: false });
        return () => el.removeEventListener("wheel", rueda);
    }, [z.k]);

    const abajo = (e) => {
        if (z.k === 1 || e.button > 0) return;
        arrastre.current = { x: e.clientX, y: e.clientY, ox: z.x, oy: z.y };
        huboArrastre.current = false;
    };
    const mueve = (e) => {
        const a = arrastre.current; if (!a) return;
        const dx = e.clientX - a.x, dy = e.clientY - a.y;
        if (!huboArrastre.current && Math.hypot(dx, dy) < 5) return;
        huboArrastre.current = true;
        const { width: w, height: h } = caja.current.getBoundingClientRect();
        setZ(p => limitar(p.k, a.ox + dx, a.oy + dy, w, h));
    };
    const suelta = () => { arrastre.current = null; };
    const clicCapturado = (e) => { if (huboArrastre.current) { e.stopPropagation(); e.preventDefault(); huboArrastre.current = false; } };
    const teclas = (e) => {
        if (e.key === "+" || e.key === "=") { e.preventDefault(); zoomEn(1.3, ...centro()); }
        else if (e.key === "-") { e.preventDefault(); zoomEn(1 / 1.3, ...centro()); }
        else if (e.key === "0") setZ({ k: 1, x: 0, y: 0 });
    };

    return (
        <div ref={raiz} className={pantalla ? "conzoom-pantalla" : undefined} style={{ position: "relative", background: pantalla ? "var(--cui-body-bg, #fff)" : undefined, display: pantalla ? "flex" : undefined, alignItems: "center" }}>
            {pantalla && <style>{".conzoom-pantalla svg { max-height: 96vh !important; } .conzoom-pantalla > div:first-of-type { width: 100%; }"}</style>}
            <div ref={caja} tabIndex={0} onKeyDown={teclas} onPointerDown={abajo} onPointerMove={mueve} onPointerUp={suelta} onPointerLeave={suelta} onClickCapture={clicCapturado}
                onDoubleClick={() => setZ({ k: 1, x: 0, y: 0 })} title="Ctrl + rueda o los botones para acercar; arrastra para mover; doble clic para volver"
                style={{ overflow: "hidden", touchAction: z.k > 1 ? "none" : "auto", cursor: z.k > 1 ? "grab" : "default", outline: "none" }}>
                <div style={{ transform: `translate(${z.x}px, ${z.y}px) scale(${z.k})`, transformOrigin: "0 0" }}>{children}</div>
            </div>
            <div className="no-imprimir btn-group-vertical btn-group-sm" style={{ position: "absolute", right: 6, top: 6, opacity: 0.9 }}>
                <button type="button" className="btn btn-light border" aria-label="Acercar" title="Acercar" disabled={z.k >= MAX} onClick={() => zoomEn(1.4, ...centro())}><i className="fa fa-search-plus" /></button>
                <button type="button" className="btn btn-light border" aria-label="Alejar" title="Alejar" disabled={z.k <= MIN} onClick={() => zoomEn(1 / 1.4, ...centro())}><i className="fa fa-search-minus" /></button>
                <button type="button" className="btn btn-light border" aria-label={pantalla ? "Salir de pantalla completa" : "Pantalla completa"} title={pantalla ? "Salir de pantalla completa" : "Pantalla completa"} onClick={alternarPantalla}><i className={`fa fa-${pantalla ? "compress" : "expand"}`} /></button>
                <button type="button" className="btn btn-light border" aria-label="Descargar imagen" title="Descargar como imagen PNG" onClick={() => { const sv = caja.current && caja.current.querySelector("svg"); if (sv) svgAPng(sv, "diagrama-aws.png"); }}><i className="fa fa-download" /></button>
                <button type="button" className="btn btn-light border" aria-label="Tamaño original" title="Tamaño original" disabled={z.k === 1} onClick={() => setZ({ k: 1, x: 0, y: 0 })}><i className="fa fa-compress-arrows-alt" /></button>
            </div>
        </div>
    );
};

export default ConZoom;
